import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Box, SimpleGrid, Group } from '@mantine/core'
import { format } from 'date-fns'
import LoadingComponent from '../../app/layout/LoadingComponents'
import { AppBadge, AppButton, AppCard, AppText } from '../../app/common/ui'
import { useStore } from '../../app/stores/store'
import { Firearm } from '../../app/models/firearm'

function formatCurrency (value: number | null | undefined) {
  if (value == null) return '—'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value)
}

function getTotalValue (f: Firearm): number {
  const firearmValue = f.currentValue ?? 0
  const partsValue = (f.parts ?? []).reduce((sum, p) => sum + (p.value || 0), 0)
  return firearmValue + partsValue
}

export default observer(function Inventory () {
  const { firearmStore, userStore } = useStore()
  const { loadFirearms, firearmRegistry, loadingInitial } = firearmStore

  useEffect(() => {
    if (firearmRegistry.size === 0) loadFirearms()
  }, [firearmRegistry.size, loadFirearms])

  if (loadingInitial) {
    return <LoadingComponent content="Loading inventory..." />
  }

  const firearms = Array.from(firearmRegistry.values()).sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  )
  const totalCollectionValue = firearms.reduce((sum, f) => sum + getTotalValue(f), 0)

  if (firearms.length === 0) {
    return (
      <Box>
        <Box mb="xl">
          <AppText sx={{ fontFamily: 'Oswald, sans-serif', fontWeight: 700, fontSize: 22, color: 'var(--gs-text)', marginBottom: 'var(--gs-xs)' }}>
            INVENTORY
          </AppText>
          <AppText size="sm" sx={{ fontFamily: 'DM Sans, sans-serif', color: 'var(--gs-muted)' }}>
            Track condition, value, and parts for your firearms
          </AppText>
        </Box>
        <Box
          py="xl"
          px="lg"
          sx={{
            textAlign: 'center',
            background: 'var(--gs-surface)',
            borderRadius: 8,
            border: '1px solid var(--gs-border)'
          }}
        >
          <AppText size="lg" color="dimmed" mb="md">
            No items in inventory yet. Add firearms from the Gun Vault to track condition and value.
          </AppText>
          {userStore.isLoggedIn && (
            <AppButton
              component={Link}
              to="/createFirearm"
              size="sm"
              sx={{ background: 'var(--gs-accent)', color: '#fff', fontFamily: 'DM Sans, sans-serif', '&:hover': { background: '#bf4a00' } }}
            >
              Add firearm
            </AppButton>
          )}
        </Box>
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 'var(--gs-md)', marginBottom: 'var(--gs-lg)' }}>
        <Box>
          <AppText sx={{ fontFamily: 'Oswald, sans-serif', fontWeight: 700, fontSize: 22, color: 'var(--gs-text)', marginBottom: 'var(--gs-xs)' }}>
            INVENTORY
          </AppText>
          <AppText size="sm" sx={{ fontFamily: 'DM Sans, sans-serif', color: 'var(--gs-muted)' }}>
            Track condition, value, and parts for your firearms
          </AppText>
        </Box>
        {userStore.isLoggedIn && (
          <AppButton
            component={Link}
            to="/createFirearm"
            size="sm"
            sx={{ background: 'var(--gs-accent)', color: '#fff', fontFamily: 'DM Sans, sans-serif', '&:hover': { background: '#bf4a00' } }}
          >
            Add to inventory
          </AppButton>
        )}
      </Box>

      <Box
        mb="xl"
        sx={{
          display: 'flex',
          gap: 'var(--gs-md)',
          flexWrap: 'wrap'
        }}
      >
        <Box
          px="md"
          py="sm"
          sx={{
            background: 'var(--gs-surface)',
            border: '1px solid var(--gs-border)',
            borderRadius: 8,
            minWidth: 140
          }}
        >
          <AppText size="xs" color="dimmed" sx={{ textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 }}>
            Total value
          </AppText>
          <AppText size="xl" weight={700} sx={{ fontVariantNumeric: 'tabular-nums', color: 'var(--gs-gold)' }}>
            {formatCurrency(totalCollectionValue)}
          </AppText>
        </Box>
        <Box
          px="md"
          py="sm"
          sx={{
            background: 'var(--gs-surface)',
            border: '1px solid var(--gs-border)',
            borderRadius: 8,
            minWidth: 100
          }}
        >
          <AppText size="xs" color="dimmed" sx={{ textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 }}>
            Items
          </AppText>
          <AppText size="xl" weight={700}>
            {firearms.length}
          </AppText>
        </Box>
      </Box>

      <SimpleGrid cols={1} breakpoints={[{ minWidth: 640, cols: 2 }, { minWidth: 960, cols: 3 }]} spacing="lg">
        {firearms.map((firearm) => {
          const displayImage = firearm.images?.find((i) => i.isMain)?.url || firearm.coverImageUrl
          const totalValue = getTotalValue(firearm)
          const partsCount = firearm.parts?.length ?? 0

          return (
            <AppCard
              key={firearm.id}
              component={Link}
              to={`/inventory/${firearm.id}`}
              padding="md"
              radius="md"
              sx={{
                textDecoration: 'none',
                color: 'inherit',
                background: 'var(--gs-surface)',
                border: '1px solid var(--gs-border)',
                borderRadius: 8,
                transition: 'border-color 150ms',
                '&:hover': { borderColor: 'var(--gs-border-bright)' }
              }}
            >
              {displayImage && (
                <Box mb="sm">
                  <img
                    src={displayImage}
                    alt={`${firearm.manufacturer} ${firearm.model}`}
                    style={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 8 }}
                  />
                </Box>
              )}
              <Group position="apart" mb="xs">
                <AppText weight={600} size="md" lineClamp={1}>
                  {firearm.manufacturer} {firearm.model}
                </AppText>
                <Group spacing="xs">
                  {firearm.type && (
                    <AppBadge size="sm" variant="outline" radius="xl">
                      {firearm.type}
                    </AppBadge>
                  )}
                  {firearm.conditionGrade && (
                    <AppBadge size="sm" variant="light" radius="xl">
                      {firearm.conditionGrade}
                    </AppBadge>
                  )}
                </Group>
              </Group>
              {firearm.serialNumber && (
                <AppText size="xs" color="dimmed" mb={4}>
                  SN: {firearm.serialNumber}
                </AppText>
              )}
              <Group position="apart" mt="sm">
                <AppText size="sm" color="dimmed">
                  {formatCurrency(firearm.currentValue)} base
                  {partsCount > 0 && ` · ${partsCount} parts`}
                </AppText>
                <AppText weight={700} size="md" sx={{ color: 'var(--gs-gold)', fontVariantNumeric: 'tabular-nums' }}>
                  {formatCurrency(totalValue)}
                </AppText>
              </Group>
              <AppText size="xs" color="dimmed" mt="xs">
                Added {format(new Date(firearm.createdAt), 'MMM d, yyyy')}
              </AppText>
            </AppCard>
          )
        })}
      </SimpleGrid>
    </Box>
  )
})
